import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  History,
  Mail,
  Phone,
  Users,
  Search,
  Calendar,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Filter
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HistoryPage = () => {
  const { getAuthHeader } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchHistory();
  }, [filter]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", "100");
      if (filter !== "all") {
        params.append("lookup_type", filter);
      }
      
      const response = await axios.get(`${API}/user/history?${params.toString()}`, {
        headers: getAuthHeader()
      });
      setHistory(response.data);
    } catch (error) {
      toast.error("Failed to load investigation history");
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "email": return <Mail className="w-4 h-4 text-primary" />;
      case "phone": return <Phone className="w-4 h-4 text-emerald-500" />;
      case "social": return <Users className="w-4 h-4 text-purple-500" />;
      default: return <Search className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      email: "bg-primary/10 text-primary border-primary/20",
      phone: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      social: "bg-purple-500/10 text-purple-500 border-purple-500/20"
    };
    return styles[type] || "bg-muted text-muted-foreground";
  };

  const getRiskLevel = (result) => {
    const score = result?.fraud_score || 0;
    if (score >= 70) return { label: "High Risk", class: "bg-red-500/10 text-red-500 border-red-500/20", icon: XCircle };
    if (score >= 40) return { label: "Medium Risk", class: "bg-amber-500/10 text-amber-500 border-amber-500/20", icon: AlertTriangle };
    return { label: "Low Risk", class: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20", icon: CheckCircle };
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredHistory = history.filter(item => 
    searchQuery === "" || 
    item.query?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <History className="w-8 h-8 text-primary" />
            <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight">
              Investigation History
            </h1>
          </div>
          <p className="text-muted-foreground text-sm">
            View and revisit your past investigations
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchHistory}
          className="gap-2"
          data-testid="refresh-history-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-card/50 border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email, phone, or query..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="history-search"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[140px]" data-testid="history-filter">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="social">Social</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Investigations</p>
            <p className="text-2xl font-mono font-bold mt-1">{history.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Email Lookups</p>
            <p className="text-2xl font-mono font-bold mt-1 text-primary">
              {history.filter(h => h.type === "email").length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Phone Lookups</p>
            <p className="text-2xl font-mono font-bold mt-1 text-emerald-500">
              {history.filter(h => h.type === "phone").length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">High Risk Found</p>
            <p className="text-2xl font-mono font-bold mt-1 text-red-500">
              {history.filter(h => (h.result?.fraud_score || 0) >= 70).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* History List */}
      <Card className="bg-card/50 border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            Recent Investigations ({filteredHistory.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredHistory.length > 0 ? (
            <div className="space-y-3">
              {filteredHistory.map((item, index) => {
                const risk = getRiskLevel(item.result);
                const RiskIcon = risk.icon;
                
                return (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors"
                    data-testid={`history-item-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-card flex items-center justify-center">
                        {getTypeIcon(item.type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium">{item.query}</span>
                          <Badge className={getTypeBadge(item.type)}>
                            {item.type}
                          </Badge>
                          {item.used_credit && (
                            <Badge variant="outline" className="text-xs">
                              Credit Used
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                          <span>{formatTimestamp(item.timestamp)}</span>
                          {item.result?.service && (
                            <span>via {item.result.service}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={risk.class}>
                        <RiskIcon className="w-3 h-3 mr-1" />
                        {item.result?.fraud_score || 0}%
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">No investigations found</p>
              <p className="text-sm mt-1">
                {searchQuery ? "Try a different search term" : "Start investigating suspicious emails and phone numbers"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoryPage;
